import React from 'react';
import PropTypes from 'prop-types';
import { getStorageItem, setStorageItem } from '@snapp/client-utils';
import ErrorFallback from '../error-fallback/error-fallback';
import t from '@/i18n';
import SentryCaptureError from '@/service/sentry/sentry-logger';
import storageKeys from '@/service/storageKeys';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, id: '' };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch = async (error, errorInfo) => {
    const { onError } = this.props;
    const id = await SentryCaptureError().captureError(error, errorInfo);
    console.error('@@error', error, errorInfo);
    this.setState({ error, id });
    onError?.(error, errorInfo);
  };

  handleReset = () => {
    const lastCrashTime = getStorageItem(storageKeys.lastCrashOccurredAt);
    const now = new Date().getTime();
    const shouldRestartApp =
      lastCrashTime &&
      Math.floor((now - lastCrashTime) / 1000) <=
        process.env.REACT_APP_RESTART_APP_TIMEOUT;

    if (shouldRestartApp) {
      localStorage.clear();
    }

    this.setState({ error: null });
    setStorageItem(storageKeys.lastCrashOccurredAt, Date.now());
    window.location.reload();
  };

  render() {
    const { error, id } = this.state;
    const { children } = this.props;
    if (error !== null) {
      return (
        <ErrorFallback
          errorDescription={t('errorFallbackDescription')}
          errorTitle={t('errorFallbackTitle')}
          onFallbackButtonClick={this.handleReset}
          id={id}
          {...this.props}
        />
      );
    }
    return children;
  }
}

ErrorBoundary.propTypes = {
  onError: PropTypes.func,
  children: PropTypes.node.isRequired,
};

ErrorBoundary.defaultProps = {
  onError: () => {},
};

export default ErrorBoundary;
